import { Button, TextInput } from 'flowbite-react';
import { useMemo, useState } from 'react';
import { FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { type ClientDto, useClientsQuery } from '../../queries/use-clients.js';
import { ClientEditModal } from './ClientEditModal.js';

/**
 * Sidebar list of tenants — the left half of the legacy `/clients`
 * split-pane. Search filters the list in-place; "+ Add New" opens the
 * {@link ClientEditModal}. Clicking a row navigates to
 * `/clients/{id}` (which the route hierarchy then redirects to the
 * default sub-tab).
 *
 * The search/filter is client-side because the canonical legacy UX
 * loaded all tenants into memory anyway — there are O(100) of them
 * across all customers, far below pagination thresholds.
 */
export function ClientsList(): JSX.Element {
  const { data, isLoading, isError, error } = useClientsQuery();
  const { id: activeId } = useParams<{ id: string }>();
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);

  const filtered = useMemo<ClientDto[]>(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    const matches = needle
      ? data.filter(
          (c) =>
            c.name.toLowerCase().includes(needle) ||
            (c.email?.toLowerCase().includes(needle) ?? false),
        )
      : data;
    return [...matches].sort((a, b) => a.name.localeCompare(b.name));
  }, [data, search]);

  return (
    <aside className="flex w-72 flex-col space-y-4 border-r border-gray-200 p-3 dark:border-gray-700">
      <div className="flex flex-row items-center space-x-2">
        <div className="relative flex w-full items-center">
          <TextInput
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            icon={FiSearch}
            className="w-full"
            aria-label="Search clients"
          />
          {search ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FiX size={20} />
            </button>
          ) : null}
        </div>
        <Button
          size="sm"
          onClick={() => setEditorOpen(true)}
          aria-label="Add new client"
          className="shrink-0"
        >
          <FiPlus className="mr-1" />
          Add New
        </Button>
      </div>

      <div className="grow overflow-y-auto" data-testid="clients-list">
        {isLoading ? (
          <p className="px-2 py-4 text-sm text-gray-500 dark:text-gray-400" role="status">
            Loading clients…
          </p>
        ) : isError ? (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {error instanceof Error ? error.message : 'Failed to load clients'}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyHint hasSearch={search.trim() !== ''} hasData={(data?.length ?? 0) > 0} />
        ) : (
          <ul className="flex flex-col">
            {filtered.map((client) => {
              const isActive = client.id === activeId;
              return (
                <li key={client.id}>
                  <Link
                    to={`/clients/${client.id}`}
                    aria-current={isActive ? 'page' : undefined}
                    className={twMerge(
                      'flex flex-row items-start space-x-2 rounded-md px-3 py-2 text-sm',
                      isActive
                        ? 'pointer-events-none bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700',
                    )}
                  >
                    <ClientLogo logoUrl={client.smallLogoUrl} />
                    <div className="flex grow flex-col overflow-hidden">
                      <span className="truncate font-medium">{client.name}</span>
                      {client.email ? (
                        <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {client.email}
                        </span>
                      ) : null}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ClientEditModal open={editorOpen} onClose={() => setEditorOpen(false)} />
    </aside>
  );
}

function EmptyHint({ hasSearch, hasData }: { hasSearch: boolean; hasData: boolean }): JSX.Element {
  if (hasSearch && hasData) {
    return (
      <p className="px-2 py-4 text-sm text-gray-500 dark:text-gray-400">
        No clients match that search.
      </p>
    );
  }
  return (
    <p className="px-2 py-4 text-sm text-gray-500 dark:text-gray-400">
      No clients yet. Click <span className="font-medium">Add New</span> to onboard a tenant.
    </p>
  );
}

function ClientLogo({ logoUrl }: { logoUrl: string | null }): JSX.Element {
  return (
    <div className="min-h-8 min-w-8 rounded-md border border-gray-300 bg-gray-200 p-1 dark:border-gray-700 dark:bg-gray-600">
      {logoUrl ? <img src={logoUrl} alt="" className="h-6 w-6 rounded-sm object-contain" /> : null}
    </div>
  );
}
